import {
  AreaConfigResponse,
  Model,
  ModelParameter,
  ModelType,
  ProductionStream,
} from 'types/dca-scenarios-api/responses';
import type { ProductionDataSource } from 'types/api-responses';
import {
  BiasedProductionStreamConfigData,
  EarlyTimeProductionStreamConfigData,
  ProductionStreamConfigData,
  RampUpProductionStreamConfigData,
  UnbiasedProductionStreamConfigData,
} from './types';
import { getMethodParameterMapping } from './utils';

type ParameterBounds = {
  initialGuess: Partial<Record<ModelParameter, number>>;
  upperBounds: Partial<Record<ModelParameter, number>>;
  lowerBounds: Partial<Record<ModelParameter, number>>;
  lockedParameters: Partial<Record<ModelParameter, number | null>>;
};

const modelParameterDefaults: Record<
  ModelType,
  Record<Model, ParameterBounds>
> = {
  biased: {
    modified_hyperbolic: {
      initialGuess: { qi: 1, di: 50, bFactor: 1, dlim: 7 },
      lowerBounds: { qi: 0.01, di: 7, bFactor: 0.1, dlim: 7 },
      upperBounds: { qi: 3, di: 99, bFactor: 2, dlim: 7 },
      lockedParameters: { qi: null, di: null, bFactor: null, dlim: 7 },
    },
    hyperbolic: {
      initialGuess: { qi: 1, di: 50, bFactor: 1 },
      lowerBounds: { qi: 0.01, di: 7, bFactor: 0.1 },
      upperBounds: { qi: 3, di: 99, bFactor: 2 },
      lockedParameters: { qi: null, di: null, bFactor: null },
    },
    exponential: {
      initialGuess: { qi: 1, di: 50 },
      lowerBounds: { qi: 0.01, di: 7 },
      upperBounds: { qi: 3, di: 99 },
      lockedParameters: { qi: null, di: null },
    },
    ratio: {
      initialGuess: { ri: 0.01, tElf: 1, m: -1 },
      lowerBounds: { ri: 0.01, tElf: 1, m: -1 },
      upperBounds: { ri: 0.01, tElf: 1, m: -1 },
      lockedParameters: { ri: null, tElf: null, m: null },
    },
    linear: {
      initialGuess: { qi: 1, n: 0 },
      lowerBounds: { qi: 0.01, n: 0 },
      upperBounds: { qi: 3, n: 0 },
      lockedParameters: { qi: null, n: null },
    },
    constant: {
      initialGuess: { qi: 1 },
      lowerBounds: { qi: 0.01 },
      upperBounds: { qi: 3 },
      lockedParameters: { qi: null },
    },
  },
  unbiased: {
    modified_hyperbolic: {
      initialGuess: { qi: 1, di: 60, bFactor: 1, dlim: 7 },
      lowerBounds: { qi: 0.01, di: 8, bFactor: 0.01, dlim: 7 },
      upperBounds: { qi: 3, di: 99, bFactor: 2, dlim: 7 },
      lockedParameters: { qi: null, di: null, bFactor: null, dlim: 7 },
    },
    hyperbolic: {
      initialGuess: { qi: 1, di: 60, bFactor: 1 },
      lowerBounds: { qi: 0.01, di: 8, bFactor: 0.01 },
      upperBounds: { qi: 3, di: 99, bFactor: 2 },
      lockedParameters: { qi: null, di: null, bFactor: null },
    },
    exponential: {
      initialGuess: { qi: 1, di: 60 },
      lowerBounds: { qi: 0.01, di: 8 },
      upperBounds: { qi: 3, di: 99 },
      lockedParameters: { qi: null, di: null },
    },
    ratio: {
      initialGuess: { ri: 0.01, tElf: 1, m: -1 },
      lowerBounds: { ri: 0.01, tElf: 1, m: -1 },
      upperBounds: { ri: 0.01, tElf: 1, m: -1 },
      lockedParameters: { ri: null, tElf: null, m: null },
    },
    linear: {
      initialGuess: { qi: 1, n: 0 },
      lowerBounds: { qi: 0.01, n: 0 },
      upperBounds: { qi: 3, n: 0 },
      lockedParameters: { qi: null, n: null },
    },
    constant: {
      initialGuess: { qi: 1 },
      lowerBounds: { qi: 0.01 },
      upperBounds: { qi: 3 },
      lockedParameters: { qi: null },
    },
  },
  ramp_up: {
    modified_hyperbolic: {
      initialGuess: { qi: 1, di: 50, bFactor: 1, dlim: 7 },
      lowerBounds: { qi: 0.01, di: 7, bFactor: 0.1, dlim: 7 },
      upperBounds: { qi: 3, di: 99, bFactor: 2, dlim: 7 },
      lockedParameters: { qi: null, di: null, bFactor: null, dlim: 7 },
    },
    hyperbolic: {
      initialGuess: { qi: 1, di: 50, bFactor: 1 },
      lowerBounds: { qi: 0.01, di: 7, bFactor: 0.1 },
      upperBounds: { qi: 3, di: 99, bFactor: 2 },
      lockedParameters: { qi: null, di: null, bFactor: null },
    },
    exponential: {
      initialGuess: { qi: 1, n: 0 },
      lowerBounds: { qi: 0.01, n: 0 },
      upperBounds: { qi: 3, n: 0 },
      lockedParameters: { qi: null, n: null },
    },
    ratio: {
      initialGuess: { ri: 0.01, tElf: 1, m: -1 },
      lowerBounds: { ri: 0.01, tElf: 1, m: -1 },
      upperBounds: { ri: 0.01, tElf: 1, m: -1 },
      lockedParameters: { ri: null, tElf: null, m: null },
    },
    linear: {
      initialGuess: { qi: 1, n: 0 },
      lowerBounds: { qi: 0.01, n: 0 },
      upperBounds: { qi: 3, n: 0 },
      lockedParameters: { qi: null, n: null },
    },
    constant: {
      initialGuess: { qi: 1 },
      lowerBounds: { qi: 0.01 },
      upperBounds: { qi: 3 },
      lockedParameters: { qi: null },
    },
  },
  early_time: {
    modified_hyperbolic: {
      initialGuess: { qi: 1, di: 60, bFactor: 1, dlim: 7 },
      lowerBounds: { qi: 0.01, di: 8, bFactor: 0.5, dlim: 3 },
      upperBounds: { qi: 3, di: 99, bFactor: 1.8, dlim: 10 },
      lockedParameters: { qi: null, di: null, bFactor: null, dlim: 7 },
    },
    hyperbolic: {
      initialGuess: { qi: 1, di: 60, bFactor: 1 },
      lowerBounds: { qi: 0.01, di: 8, bFactor: 0.5 },
      upperBounds: { qi: 3, di: 99, bFactor: 1.8 },
      lockedParameters: { qi: null, di: null, bFactor: null },
    },
    exponential: {
      initialGuess: { qi: 1, di: 60 },
      lowerBounds: { qi: 0.01, di: 8 },
      upperBounds: { qi: 3, di: 99 },
      lockedParameters: { qi: null, di: null },
    },
    ratio: {
      initialGuess: { ri: 0.01, tElf: 1, m: -1 },
      lowerBounds: { ri: 0.01, tElf: 1, m: -1 },
      upperBounds: { ri: 0.01, tElf: 1, m: -1 },
      lockedParameters: { ri: null, tElf: null, m: null },
    },
    linear: {
      initialGuess: { qi: 1, n: 0 },
      lowerBounds: { qi: 0.01, n: 0 },
      upperBounds: { qi: 3, n: 0 },
      lockedParameters: { qi: null, n: null },
    },
    constant: {
      initialGuess: { qi: 1 },
      lowerBounds: { qi: 0.01 },
      upperBounds: { qi: 3 },
      lockedParameters: { qi: null },
    },
  },
};

export const getDefaultParameterValuesByType = (
  model: Model,
  modelType: ModelType,
  type: 'initialGuess' | 'upperBounds' | 'lowerBounds' | 'lockedParameters'
): Partial<Record<ModelParameter, number | null>> => {
  return modelParameterDefaults[modelType][model][type];
};

export const defaultDistributions = {
  qi: 'normal',
  di: 'normal',
  bFactor: 'normal',
  dlim: 'normal',
  ri: 'normal',
  tElf: 'normal',
  m: 'normal',
  n: 'normal',
  eurMbbl: 'normal',
};

const defaultBiasedModelValues: Omit<
  BiasedProductionStreamConfigData,
  'stream'
> = {
  id: null,
  model: 'modified_hyperbolic',
  modelType: 'biased',
  maxMonthsOnline: 1000,
  qiBoundType: 'relative',
  lossFunction: 'cauchy',
  residualFunction: 'log',
  alpha: 1,
  lowerBounds: getDefaultParameterValuesByType(
    'modified_hyperbolic',
    'biased',
    'lowerBounds'
  ) as Record<ModelParameter | 'eurMbbl', number>,
  upperBounds: getDefaultParameterValuesByType(
    'modified_hyperbolic',
    'biased',
    'upperBounds'
  ) as Record<ModelParameter | 'eurMbbl', number>,
  lockedParameters: {
    ...getDefaultParameterValuesByType(
      'modified_hyperbolic',
      'biased',
      'lockedParameters'
    ),
    eurMbbl: 0,
  } as Record<ModelParameter | 'eurMbbl', number | null>,
  distributions: defaultDistributions,
  residualSigma: 0.2,
  probabilityDistance: 0.9,
  constrainEur: false,
  initialGuess: getDefaultParameterValuesByType(
    'modified_hyperbolic',
    'biased',
    'initialGuess'
  ) as Record<ModelParameter | 'eurMbbl', number>,
};
const defaultUnbiasedModelValues: Omit<
  UnbiasedProductionStreamConfigData,
  'stream'
> = {
  id: null,
  modelType: 'unbiased',
  model: 'hyperbolic',
  qiBoundType: 'relative',
  lossFunction: 'cauchy',
  residualFunction: 'log',
  alpha: 1,
  maxMonthsOnline: 10000,
  initialGuess: getDefaultParameterValuesByType(
    'hyperbolic',
    'unbiased',
    'initialGuess'
  ) as Record<ModelParameter, number>,
  lowerBounds: getDefaultParameterValuesByType(
    'hyperbolic',
    'unbiased',
    'lowerBounds'
  ) as Record<ModelParameter, number>,
  upperBounds: getDefaultParameterValuesByType(
    'hyperbolic',
    'unbiased',
    'upperBounds'
  ) as Record<ModelParameter, number>,
  lockedParameters: getDefaultParameterValuesByType(
    'hyperbolic',
    'unbiased',
    'lockedParameters'
  ) as Record<ModelParameter, number | null>,
};
const defaultRampUpModelValues: Omit<
  RampUpProductionStreamConfigData,
  'stream'
> = {
  id: null,
  modelType: 'ramp_up',
  model: 'constant',
  qiBoundType: 'relative',
  lossFunction: 'huber',
  residualFunction: 'log',
  alpha: 1,
  earlyTimeRampUpOnly: true,
  maxMonthsOnline: 10000,
  initialGuess: getDefaultParameterValuesByType(
    'constant',
    'ramp_up',
    'initialGuess'
  ) as Record<ModelParameter, number>,
  lowerBounds: getDefaultParameterValuesByType(
    'constant',
    'ramp_up',
    'lowerBounds'
  ) as Record<ModelParameter, number>,
  upperBounds: getDefaultParameterValuesByType(
    'constant',
    'ramp_up',
    'upperBounds'
  ) as Record<ModelParameter, number>,
  lockedParameters: getDefaultParameterValuesByType(
    'constant',
    'ramp_up',
    'lockedParameters'
  ) as Record<ModelParameter, number | null>,
};
const defaultEarlyTimeModelValues: Omit<
  EarlyTimeProductionStreamConfigData,
  'stream'
> = {
  id: null,
  modelType: 'early_time',
  model: 'modified_hyperbolic',
  qiBoundType: 'relative',
  lossFunction: 'mlle',
  residualFunction: 'log',
  alpha: 1,
  maxMonthsOnline: 12,
  residualSigma: 0.2,
  probabilityDistance: 0.9,
  eurToleranceFactor: 0.25,
  lateralLengthDegradationFactor: 0,
  lateralLengthDegradationFt: 2500,
  initialGuess: getDefaultParameterValuesByType(
    'modified_hyperbolic',
    'early_time',
    'initialGuess'
  ) as Record<ModelParameter, number>,
  lowerBounds: getDefaultParameterValuesByType(
    'modified_hyperbolic',
    'early_time',
    'lowerBounds'
  ) as Record<ModelParameter, number>,
  upperBounds: getDefaultParameterValuesByType(
    'modified_hyperbolic',
    'early_time',
    'upperBounds'
  ) as Record<ModelParameter, number>,
  lockedParameters: getDefaultParameterValuesByType(
    'modified_hyperbolic',
    'early_time',
    'lockedParameters'
  ) as Record<ModelParameter, number | null>,
};

export const getModelDefaultValues = (
  modelType: string,
  stream: ProductionStream,
  maxMonthsOnline: number = 10000
) => {
  switch (modelType) {
    case 'biased':
      return {
        ...defaultBiasedModelValues,
        id: null,
        stream,
        maxMonthsOnline: maxMonthsOnline,
      };
    case 'unbiased':
      return {
        ...defaultUnbiasedModelValues,
        id: null,
        stream,
        maxMonthsOnline: maxMonthsOnline,
      };
    case 'ramp_up':
      return {
        ...defaultRampUpModelValues,
        id: null,
        stream,
        maxMonthsOnline: maxMonthsOnline,
      };
    default:
      return {
        ...defaultEarlyTimeModelValues,
        id: null,
        stream,
        maxMonthsOnline: maxMonthsOnline,
      };
  }
};

export const scenarioFormDefaultValues = {
  globalConfig: {
    timeFormat: 'monthly',
    qiFormat: 'monthly',
    diFormat: 'effective',
    notes: '',
    name: '',
    durationOption: 'production_start',
    durationFormat: 'yearly',
    durationPeriod: 30,
    createdBy: '',
  },
  areaConfigs: {
    areaGeometryId: null,
    areaGeometryName: null,
    formation: null,
    developmentType: 'ALL',
  },
  generalConfig: {
    id: null,
    historyCutoffMonths: 1000,
    earlyTimeCutoffMonths: 12,
    detectPeakRate: false,
    peakRateSearchWindow: 0.5,
    // Daily production first, when a well has it.
    productionDataSourceHierarchy: ['scada', 'ihs'] as ProductionDataSource[],
  },

  segmentationConfig: {
    id: null,
    method: 'none',
    targetSegment: 'first',
    fallbackToPreviousSegment: false,
    parameters: {
      model: 'l2',
      penalty: 250,
      segmentMinLength: 10,
      jumpSize: 5,
    },
  },
  outlierConfig: {
    id: null,
    method: 'none',
    parameters: {
      contamination: 0.1,
      probabilityThreshold: 0.5,
      numIterations: 10,
    },
  },
  oilStreamConfigs: [
    { ...defaultBiasedModelValues, stream: 'oil' },
  ] as ProductionStreamConfigData[],
  gasStreamConfigs: [
    { ...defaultBiasedModelValues, stream: 'gas' },
  ] as ProductionStreamConfigData[],
  waterStreamConfigs: [
    { ...defaultBiasedModelValues, stream: 'water' },
  ] as ProductionStreamConfigData[],
};

export const defaultModelConfigValues: Record<
  ModelType,
  Omit<ProductionStreamConfigData, 'stream'>
> = {
  biased: defaultBiasedModelValues,
  unbiased: defaultUnbiasedModelValues,
  ramp_up: defaultRampUpModelValues,
  early_time: defaultEarlyTimeModelValues,
};

export const INITIAL_CONFIG: AreaConfigResponse = {
  id: 'local',
  href: '',
  auth: [],
  dcaScenarioId: '',
  developmentType: 'ALL',
  areaGeometryId: null,
  areaGeometryGeoJson: null,
  formation: null,
  createdAt: '',
  updatedAt: '',
  name: 'Global',
  areaGeometryName: null,
  generalConfig: {
    id: '',
    name: '',
    href: '',
    auth: [],
  },
  segmentationConfig: {
    id: '',
    href: '',
    auth: [],
    name: '',
  },
  outlierConfig: {
    id: '',
    href: '',
    auth: [],
    name: '',
  },
  resources: {
    productionStreamConfigs: {
      href: '',
      auth: [],
    },
    dcaScenario: {
      href: '',
      auth: [],
    },
    generalConfigs: {
      href: '/dca_scenarios/general_configs',
      auth: ['read'],
    },
    segmentationConfigs: {
      href: '/dca_scenarios/segmentation_configs',
      auth: ['read'],
    },
    outlierConfigs: {
      href: '/dca_scenarios/outlier_configs',
      auth: ['read'],
    },
    areaGeometry: {
      href: '',
      auth: [],
    },
  },
};

export const getDefaultParameterValues = (
  modelType: ModelType,
  model: Model
) => {
  const paramKeys = getMethodParameterMapping(modelType)[model];
  const distributions = {} as Record<ModelParameter, string>;

  paramKeys.forEach((key) => {
    distributions[key as ModelParameter] = 'normal';
  });

  return {
    initialGuess: getDefaultParameterValuesByType(
      model,
      modelType,
      'initialGuess'
    ),
    lowerBounds: getDefaultParameterValuesByType(
      model,
      modelType,
      'lowerBounds'
    ),
    upperBounds: getDefaultParameterValuesByType(
      model,
      modelType,
      'upperBounds'
    ),
    distributions,
  };
};
